// ============================================================================
// OBT — Kampanya FİZİKSEL TAM silme diyaloğu (admin-flag'li; geri dönüş YOK)
// ============================================================================
// Backend sözleşmesi: POST /obt/campaigns/{id}/hard-delete. Kampanya + TÜM alt
// verisi DB'den KALICI olarak kaldırılır (geri alınamaz). Yalnız `obt_allow_hard_delete`
// açıkken ve yetkili (admin ∨ sahibi) için çağrılır. Yanlışlıkla silmeyi önlemek
// için kullanıcı kampanya BAŞLIĞINI birebir yazmadan buton etkinleşmez.
// ============================================================================

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Loader2, Trash2, AlertTriangle } from 'lucide-react';
import { hardDeleteCampaign } from './campaignApi';
import { normalizeObtError } from '../obtErrors';

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {{campaign_id:string, title?:string}|null} props.campaign
 * @param {() => void} props.onClose
 * @param {() => void} [props.onDeleted]
 */
export default function HardDeleteCampaignDialog({ open, campaign, onClose, onDeleted }) {
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setConfirmText('');
      setSubmitting(false);
      setError(null);
    }
  }, [open]);

  const title = campaign?.title || '';
  const matches = confirmText.trim() === title.trim() && title.trim().length > 0;

  const handleHardDelete = async () => {
    if (!campaign?.campaign_id || !matches || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await hardDeleteCampaign(campaign.campaign_id);
      if (onDeleted) onDeleted();
      onClose();
    } catch (e) {
      setError(normalizeObtError(e));
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !submitting) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="h-5 w-5" aria-hidden="true" />
            Kalıcı (fiziksel) sil
          </DialogTitle>
          <DialogDescription>
            {title ? `“${title}”` : 'Bu bilgi talebi'} ve tüm verisi (cevaplar, incelemeler,
            geçmiş) veritabanından <strong>kalıcı olarak</strong> silinecek. Bu işlem{' '}
            <strong>GERİ ALINAMAZ</strong>. Yalnız kimin ne zaman sildiği kaydı denetim
            günlüğünde saklanır.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="obt-harddelete-confirm">
            Onaylamak için bilgi talebi başlığını birebir yazın:
          </Label>
          <p className="select-none rounded bg-gray-100 px-2 py-1 text-sm font-medium text-gray-700">
            {title}
          </p>
          <Input
            id="obt-harddelete-confirm"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Bilgi talebi başlığını buraya yazın"
            autoComplete="off"
          />
        </div>

        {error && (
          <p className="text-sm text-red-600" role="alert">
            {error.message || 'Bilgi talebi silinemedi.'}
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Vazgeç
          </Button>
          <Button
            className="bg-red-600 hover:bg-red-700"
            onClick={handleHardDelete}
            disabled={!matches || submitting}
          >
            {submitting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
            )}
            Kalıcı olarak sil
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
